'use client';

import type { EmblaOptionsType } from 'embla-carousel';
import { ChevronLeft, ChevronRight } from '@v2/ui/Icon/Arrow';
import { useCallback, useEffect, useRef, useState } from 'react';

type Props = {
  options?: EmblaOptionsType;
  children: React.ReactNode;
};

export const Carousel = ({
  options: _options = { slidesToScroll: 'auto' },
  children,
}: Props) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canScrollBackward, setCanScrollBackward] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const updateScrollControls = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    setCanScrollBackward(viewport.scrollLeft > 1);
    setCanScrollForward(
      viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 1
    );
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    updateScrollControls();
    viewport.addEventListener('scroll', updateScrollControls, {
      passive: true,
    });
    const observer = new ResizeObserver(updateScrollControls);
    observer.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', updateScrollControls);
      observer.disconnect();
    };
  }, [updateScrollControls]);

  const scroll = (direction: -1 | 1) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.scrollBy({
      behavior: 'smooth',
      left: direction * Math.max(viewport.clientWidth * 0.8, 288),
    });
  };

  return (
    <section className="w-full" aria-label="Carousel">
      <div className="relative">
        <div
          ref={viewportRef}
          tabIndex={0}
          role="group"
          aria-label="Carousel items"
          className="overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] motion-reduce:scroll-auto [&::-webkit-scrollbar]:hidden"
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') scroll(-1);
            if (event.key === 'ArrowRight') scroll(1);
          }}
        >
          <div className="flex snap-x snap-mandatory flex-row gap-6 [&>*]:snap-start">
            {children}
          </div>
        </div>

        <div className="absolute top-16 flex h-full min-h-fit">
          <button
            type="button"
            aria-label="Scroll carousel backward"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-800 shadow-md disabled:hidden lg:ml-[-20px]"
            onClick={() => scroll(-1)}
            disabled={!canScrollBackward}
          >
            <ChevronLeft />
          </button>
        </div>

        <div className="absolute right-0 top-16 flex h-full min-h-fit">
          <button
            type="button"
            aria-label="Scroll carousel forward"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-800 shadow-md disabled:opacity-50 lg:mr-[-20px]"
            onClick={() => scroll(1)}
            disabled={!canScrollForward}
          >
            <ChevronRight />
          </button>
        </div>
      </div>
    </section>
  );
};
